import { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2, CalendarCheck, CheckCircle2, Clock, ArrowRight, ArrowLeft, Video } from "lucide-react";
import { toast } from "sonner";
import { INTERESTS, TIMEZONES, AVAILABILITY, CONSULTATION_TYPES } from "../data";
import { useBooking } from "../BookingContext";
import DateTimePicker from "./DateTimePicker";
import PayPalButton from "./PayPalButton";
import { detectTimeZone, formatTime, tzAbbr } from "../lib/scheduling";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", email: "", phone: "", company: "", interest: "", message: "" };

export default function BookingModal() {
  const { open, setOpen, preset, presetType } = useBooking();
  const [form, setForm] = useState(EMPTY);
  const [typeId, setTypeId] = useState("free");
  const [timeZone, setTimeZone] = useState(() => detectTimeZone(TIMEZONES));
  const [slot, setSlot] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [phase, setPhase] = useState("form"); // form | pay | success

  const type = CONSULTATION_TYPES.find((t) => t.id === typeId) || CONSULTATION_TYPES[0];
  const isPaid = type.price > 0;

  let SubmitIcon = CalendarCheck;
  if (loading) SubmitIcon = Loader2;
  else if (isPaid) SubmitIcon = ArrowRight;

  useEffect(() => {
    if (open) {
      setForm({ ...EMPTY, interest: preset || "" });
      setTypeId(presetType || "free");
      setSlot("");
      setResult(null);
      setPhase("form");
    }
  }, [open, preset, presetType]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const slotSummary = slot
    ? `${formatTime(slot, timeZone)} ${tzAbbr(timeZone)} — ${new Date(slot).toLocaleDateString(undefined, { timeZone, weekday: "long", month: "long", day: "numeric" })}`
    : "";

  const submit = async (e) => {
    e.preventDefault();
    if (isPaid && !slot) {
      toast.error("Please pick a date & time so we can generate your Google Meet link.");
      return;
    }
    setLoading(true);
    try {
      const slotEnd = slot
        ? new Date(new Date(slot).getTime() + type.duration * 60000).toISOString()
        : "";
      const { data } = await axios.post(`${API}/consultation`, {
        ...form,
        interest: form.interest || "General Inquiry",
        slot_start: slot,
        slot_end: slotEnd,
        timezone: timeZone,
        consultation_type: type.label,
        duration: type.duration,
        amount: type.price,
        payment_status: isPaid ? "pending" : "not_required",
      });
      setResult(data);
      toast.success(isPaid ? "Scheduled — complete payment to confirm." : "Request received!");
      setPhase(isPaid ? "pay" : "success");
    } catch (err) {
      if (err?.response?.status === 409) {
        const msg = err.response?.data?.detail || "You've already used your free consultation.";
        toast.error(msg);
        if (!isPaid) setTypeId("standard"); // nudge toward a paid session
      } else {
        toast.error("Could not submit. Please try again or call us.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#050A15]/80 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="booking-modal"
        >
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.97 }}
            transition={{ duration: 0.3 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg bg-[#0C142A] border border-[#1D2847] max-h-[92vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-[#0C142A] border-b border-[#1D2847] px-7 py-5 flex items-center justify-between z-10">
              <div className="flex items-center gap-2">
                <CalendarCheck className="text-[#D4AF37]" size={20} />
                <h3 className="font-display text-xl font-bold tracking-tight">Book a Live Session</h3>
              </div>
              <button onClick={() => setOpen(false)} data-testid="booking-close" className="text-[#A3A8B8] hover:text-[#D4AF37]">
                <X size={22} />
              </button>
            </div>

            {/* SUCCESS (free) */}
            {phase === "success" && (
              <div className="px-7 py-14 text-center" data-testid="booking-success">
                <CheckCircle2 className="text-[#D4AF37] mx-auto" size={56} />
                <h4 className="font-display text-2xl font-bold mt-5">Request Received</h4>
                <p className="text-[#A3A8B8] mt-3 max-w-sm mx-auto">
                  Thank you! We'll confirm your free 15-minute discovery call by email within one business day.
                </p>
                {slot && (
                  <div className="mt-5 inline-flex items-center gap-2 text-sm text-[#F8F9FA] bg-[#131E3D] border border-[#D4AF37]/40 px-4 py-2">
                    <Clock size={15} className="text-[#D4AF37]" /> {slotSummary}
                  </div>
                )}
                <div className="mt-4 text-left max-w-sm mx-auto"><MeetBlock result={result} /></div>
                <button onClick={() => setOpen(false)} className="mt-8 block w-full bg-[#D4AF37] text-[#050A15] hover:bg-[#F0C84B] font-bold uppercase tracking-wider px-8 py-3 transition-colors">
                  Done
                </button>
              </div>
            )}

            {/* PAY (paid tiers) */}
            {phase === "pay" && (
              <div className="px-7 py-8" data-testid="booking-pay">
                <button onClick={() => setPhase("form")} className="inline-flex items-center gap-1 text-sm text-[#A3A8B8] hover:text-[#D4AF37] mb-5">
                  <ArrowLeft size={15} /> Back
                </button>
                <h4 className="font-display text-2xl font-bold">Complete your ${type.price} payment</h4>
                <p className="text-[#A3A8B8] mt-2">
                  {type.label} · {type.duration} minutes{slot ? ` · ${slotSummary}` : ""}
                </p>
                <div className="mt-4 bg-[#0A1124] border border-[#1D2847] p-4 text-sm text-[#C9CDD9]">
                  Your booking details are saved. Pay securely below with PayPal or a card — you'll get a PayPal
                  receipt instantly, and we'll confirm your session by email.
                </div>
                <div className="mt-5">
                  <PayPalButton hostedButtonId={type.hostedButtonId} />
                </div>
                <MeetBlock result={result} />
                <button onClick={() => setOpen(false)} className="mt-6 w-full border border-[#1D2847] text-[#A3A8B8] hover:text-[#D4AF37] hover:border-[#D4AF37]/50 font-bold uppercase tracking-wider text-sm px-6 py-3 transition-colors">
                  I'll pay later / Close
                </button>
              </div>
            )}

            {/* FORM */}
            {phase === "form" && (
              <form onSubmit={submit} className="px-7 py-6 space-y-5">
                {/* Type selector */}
                <div>
                  <span className="block text-xs uppercase tracking-wider text-[#A3A8B8] font-bold mb-3">Choose your session</span>
                  <div className="grid grid-cols-3 gap-2" data-testid="booking-type-list">
                    {CONSULTATION_TYPES.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        data-testid={`booking-type-${t.id}`}
                        onClick={() => setTypeId(t.id)}
                        className={`flex flex-col items-center text-center p-3 border transition-colors ${
                          typeId === t.id
                            ? "bg-[#D4AF37] text-[#050A15] border-[#D4AF37]"
                            : "bg-[#131E3D] text-[#F8F9FA] border-[#1D2847] hover:border-[#D4AF37]/50"
                        }`}
                      >
                        <span className="font-display font-bold text-sm leading-tight">{t.duration} min</span>
                        <span className={`text-lg font-black ${typeId === t.id ? "text-[#050A15]" : "gold-text"}`}>
                          {t.price === 0 ? "Free" : `$${t.price}`}
                        </span>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-[#A3A8B8] mt-2">{type.blurb}</p>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Full Name *" testid="booking-name">
                    <input required value={form.name} onChange={update("name")} className={inputCls} placeholder="Your name" />
                  </Field>
                  <Field label="Email *" testid="booking-email">
                    <input required type="email" value={form.email} onChange={update("email")} className={inputCls} placeholder="you@email.com" />
                  </Field>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Phone *" testid="booking-phone">
                    <input required type="tel" value={form.phone} onChange={update("phone")} className={inputCls} placeholder="(000) 000-0000" />
                  </Field>
                  <Field label="Company / Org" testid="booking-company">
                    <input value={form.company} onChange={update("company")} className={inputCls} placeholder="Optional" />
                  </Field>
                </div>

                <Field label="I'm interested in" testid="booking-interest">
                  <select value={form.interest} onChange={update("interest")} className={`${inputCls} appearance-none`}>
                    <option value="">Select a service…</option>
                    {INTERESTS.map((i) => (
                      <option key={i} value={i}>{i}</option>
                    ))}
                  </select>
                </Field>

                <div className="border border-[#1D2847] bg-[#0A1124] p-4">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#D4AF37] font-bold mb-4">
                    <CalendarCheck size={14} /> Pick a Time (Mon–Thu, 9–5 Central)
                  </div>
                  <DateTimePicker timeZone={timeZone} setTimeZone={setTimeZone} value={slot} onChange={setSlot} />
                  {slot && (
                    <div className="mt-4 flex items-center gap-2 text-sm text-[#F8F9FA] bg-[#131E3D] border border-[#D4AF37]/40 px-4 py-2" data-testid="booking-slot-summary">
                      <CheckCircle2 size={15} className="text-[#D4AF37]" /> {slotSummary}
                    </div>
                  )}
                </div>

                <Field label="Message" testid="booking-message">
                  <textarea rows={3} value={form.message} onChange={update("message")} className={`${inputCls} resize-none`} placeholder="Tell us about your goals or team…" />
                </Field>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="booking-submit"
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#050A15] hover:bg-[#F0C84B] font-bold uppercase tracking-wider px-6 py-4 transition-colors disabled:opacity-60"
                >
                  <SubmitIcon size={18} className={loading ? "animate-spin" : undefined} />
                  {isPaid ? `Continue to Payment · $${type.price}` : "Request My Free Session"}
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const inputCls =
  "w-full bg-[#131E3D] border border-[#1D2847] text-[#F8F9FA] focus:border-[#D4AF37] focus:outline-none px-4 py-3 placeholder:text-[#A3A8B8]";

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <span className="block text-xs uppercase tracking-wider text-[#A3A8B8] font-bold mb-2">{label}</span>
      {children}
    </label>
  );
}

function MeetBlock({ result }) {
  if (result?.meet_link) {
    return (
      <div data-testid="booking-meet-link" className="mt-4 bg-[#0A1124] border border-[#D4AF37]/40 p-4">
        <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#D4AF37] font-bold mb-2">
          <Video size={14} /> Your Google Meet link
        </div>
        <a href={result.meet_link} target="_blank" rel="noopener noreferrer" className="text-[#F8F9FA] underline break-all hover:text-[#D4AF37]">
          {result.meet_link}
        </a>
        <p className="text-xs text-[#A3A8B8] mt-2">A calendar invite with this link has also been emailed to you.</p>
      </div>
    );
  }
  if (result && result.slot_start) {
    return (
      <div className="mt-4 bg-[#0A1124] border border-[#1D2847] p-4 text-sm text-[#A3A8B8] flex items-center gap-2">
        <Video size={15} className="text-[#D4AF37]" /> Your Google Meet link will be emailed once your session is confirmed.
      </div>
    );
  }
  return null;
}

